import { useState, useEffect } from 'react';
import '@aws-amplify/ui-react/styles.css';
import { Backdrop, CircularProgress, Box, Grid } from '@mui/material';
import { Table } from 'components/Table';
import { defaultColumns } from './defaultColumns';
import { useTableStore } from './selectTableStore';
import { useTable } from './useTable';
import ModalTitle from '../../components/modals/ModalTitle';
import { createVideo } from '../videos/Resources';
import { useGlobalStore } from 'store/globalStore';
import { CRUD_STATES } from 'constants/app';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { SubjectLayout } from 'layouts/SubjectLayout';
import { useTableFilterStore } from 'store/tableFilterStore';
import ExportMenu from './components/ExportMenu';
import QuickFilters from './components/QuickFilters';
import PublishedAtFilter from './components/PublishedAtFilter';
import { adminAPI } from 'services/admin';

export const ReportsHome = ({
  url = '/admin/videos?limit=25&sortBy=name&offset=',
  title = '',
  listId,
  listKey = 'videoList',
  limitExceeded,
}) => {
  const { refreshData } = useGlobalStore();
  const [openTitle, setOpenTitle] = useState(false);
  const [auth, setAuth] = useOutletContext();
  const [searchParams] = useSearchParams();
  const { data, page, count, setPage, isLoading, selectedItems } =
    useTableStore();

  const { videoPageFilters, setVideoPageFilter } = useTableFilterStore();
  const { getDataTable } = useTable({
    url,
    title,
    listKey,
  });

  const [publishedAtFrom, setPublishedAtFrom] = useState(
    searchParams.get('publishedAtFrom') || ''
  );
  const [publishedAtTo, setPublishedAtTo] = useState(
    searchParams.get('publishedAtTo') || ''
  );
  const [publishedAtYear, setPublishedAtYear] = useState(
    searchParams.get('publishedAtYear') || ''
  );
  const [publishedAtMonth, setPublishedAtMonth] = useState(
    searchParams.get('publishedAtMonth') || ''
  );

  const [activeQuickKey, setActiveQuickKey] = useState(null);

  const buildPublishedAtQS = () => {
    const parts = [];
    if (publishedAtFrom)
      parts.push(`publishedAtFrom=${encodeURIComponent(publishedAtFrom)}`);
    if (publishedAtTo)
      parts.push(`publishedAtTo=${encodeURIComponent(publishedAtTo)}`);

    if (
      !publishedAtFrom &&
      !publishedAtTo &&
      publishedAtYear &&
      publishedAtMonth
    ) {
      parts.push(`publishedAtYear=${encodeURIComponent(publishedAtYear)}`);
      parts.push(`publishedAtMonth=${encodeURIComponent(publishedAtMonth)}`);
    }
    return parts.join('&');
  };

  const stripPublishedParams = (qs = '') => {
    if (!qs) return '';
    return qs
      .split('&')
      .filter(
        kv =>
          kv &&
          !kv.startsWith('publishedAtFrom=') &&
          !kv.startsWith('publishedAtTo=') &&
          !kv.startsWith('publishedAtYear=') &&
          !kv.startsWith('publishedAtMonth=')
      )
      .join('&');
  };

  const fetchPage = async ({ offset, limit = 50, signal }) => {
    const [basePath, baseQs] = url.split('?');
    const params = new URLSearchParams(baseQs || '');

    params.set('limit', String(limit));
    params.set('offset', String(offset));

    let finalPath = `${basePath}?${params.toString()}`;
    if (videoPageFilters.qs) {
      finalPath += `&${videoPageFilters.qs}`;
    }

    const resp = await adminAPI.get(finalPath, { signal });

    return {
      items: resp[listKey] || [],
      total:
        (typeof resp.totalItemsCountFiltered === 'number'
          ? resp.totalItemsCountFiltered
          : resp.total) || 0,
    };
  };

  const stripPublishedChips = (chips = []) =>
    chips.filter(
      c =>
        !['publishedAtFrom', 'publishedAtTo', 'publishedAtYm'].includes(c.field)
    );

  const toQS = (obj = {}) =>
    Object.entries(obj)
      .filter(([, v]) => v !== undefined && v !== null && v !== '')
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
      .join('&');

  const stripStatusParam = (qs = '') =>
    (qs || '')
      .split('&')
      .filter(kv => kv && !kv.startsWith('status='))
      .join('&');

  const stripStatusChip = (chips = []) =>
    (chips || []).filter(c => c.field !== 'status');

  const clearDateFilters = () => {
    setPublishedAtFrom('');
    setPublishedAtTo('');
    setPublishedAtYear('');
    setPublishedAtMonth('');

    const cleanedQS = stripPublishedParams(videoPageFilters.qs || '');
    const cleanedChips = stripPublishedChips(videoPageFilters.object || []);

    setVideoPageFilter({ qs: cleanedQS, object: cleanedChips });
    setPage(0);
    getDataTable({ page: 0, filter: cleanedQS });
  };

  useEffect(() => {
    if (publishedAtFrom || publishedAtTo) {
      if (publishedAtYear || publishedAtMonth) {
        setPublishedAtYear('');
        setPublishedAtMonth('');
      }
    }
  }, [publishedAtFrom, publishedAtTo]);

  useEffect(() => {
    if (
      publishedAtYear &&
      publishedAtMonth &&
      (publishedAtFrom || publishedAtTo)
    ) {
      setPublishedAtFrom('');
      setPublishedAtTo('');
    }
  }, [publishedAtYear, publishedAtMonth]);

  const [initialFilterData] = useState(() => {
    if (!searchParams?.size && videoPageFilters.qs) {
      return {
        qs: videoPageFilters.qs,
        object: videoPageFilters.object,
      };
    }
    if (!searchParams?.size) return;

    let queryPath = '';
    const filterInitialArray = [];

    const channel =
      searchParams.get('canal') && JSON.parse(searchParams.get('canal'));
    const unit =
      searchParams.get('unidade') && JSON.parse(searchParams.get('unidade'));

    if (channel) {
      queryPath += `&channelId=${channel.value}`;
      filterInitialArray.push({
        field: 'channel',
        label: 'Canal',
        chipValue: channel.label,
        value: channel.value,
      });
    }
    if (unit) {
      queryPath += `&unitId=${unit.value}`;

      filterInitialArray.push({
        field: 'unit',
        label: 'Unidade',
        chipValue: unit.label,
        value: unit.value,
      });
    }

    const pFrom = searchParams.get('publishedAtFrom');
    const pTo = searchParams.get('publishedAtTo');
    const pYear = searchParams.get('publishedAtYear');
    const pMonth = searchParams.get('publishedAtMonth');

    if (pFrom) {
      queryPath += `&publishedAtFrom=${encodeURIComponent(pFrom)}`;
      filterInitialArray.push({
        field: 'publishedAtFrom',
        label: 'Publicado de',
        chipValue: pFrom,
        value: pFrom,
      });
    }
    if (pTo) {
      queryPath += `&publishedAtTo=${encodeURIComponent(pTo)}`;
      filterInitialArray.push({
        field: 'publishedAtTo',
        label: 'Publicado até',
        chipValue: pTo,
        value: pTo,
      });
    }
    if (!pFrom && !pTo && pYear && pMonth) {
      queryPath += `&publishedAtYear=${encodeURIComponent(pYear)}&publishedAtMonth=${encodeURIComponent(pMonth)}`;
      filterInitialArray.push({
        field: 'publishedAtYm',
        label: 'Publicado em',
        chipValue: `${pMonth}/${pYear}`,
        value: `${pYear}-${pMonth}`,
      });
    }

    return {
      qs: queryPath,
      object: filterInitialArray,
    };
  });

  const refreshList = () => {
    setPage(0);
    const initialFilters = initialFilterData?.qs
      ? { filter: initialFilterData.qs }
      : {};
    getDataTable({ page: 0, ...initialFilters });
  };

  function updateDateFilters(pFrom, pTo, pYear, pMonth) {
    if (pFrom || pTo) {
      setPublishedAtFrom(pFrom || '');
      setPublishedAtTo(pTo || '');
      setPublishedAtYear('');
      setPublishedAtMonth('');
    } else if (pYear && pMonth) {
      setPublishedAtYear(pYear);
      setPublishedAtMonth(pMonth);
      setPublishedAtFrom('');
      setPublishedAtTo('');
    }
  }

  function buildQueryString(pFrom, pTo, pYear, pMonth, extraParams) {
    const parts = [];
    if (pFrom) parts.push(`publishedAtFrom=${encodeURIComponent(pFrom)}`);
    if (pTo) parts.push(`publishedAtTo=${encodeURIComponent(pTo)}`);
    if (!pFrom && !pTo && pYear && pMonth) {
      parts.push(`publishedAtYear=${encodeURIComponent(pYear)}`);
      parts.push(`publishedAtMonth=${encodeURIComponent(pMonth)}`);
    }
    const dateQS = parts.join('&');

    const baseQS = stripStatusParam(
      stripPublishedParams(videoPageFilters.qs || '')
    );
    const extraQS = toQS(extraParams || {});
    return [baseQS, dateQS, extraQS].filter(Boolean).join('&');
  }

  function buildChips(pFrom, pTo, pYear, pMonth, extraParams) {
    const chips = [];
    if (pFrom) {
      chips.push({
        field: 'publishedAtFrom',
        label: 'Publicado de',
        chipValue: pFrom,
        value: pFrom,
      });
    }
    if (pTo) {
      chips.push({
        field: 'publishedAtTo',
        label: 'Publicado até',
        chipValue: pTo,
        value: pTo,
      });
    }
    if (!pFrom && !pTo && pYear && pMonth) {
      chips.push({
        field: 'publishedAtYm',
        label: 'Publicado em',
        chipValue: `${pMonth}/${pYear}`,
        value: `${pYear}-${pMonth}`,
      });
    }
    if (extraParams?.status) {
      const statusLabel =
        extraParams.status === 'PUBLISHED'
          ? 'Publicado'
          : extraParams.status === 'DRAFT'
            ? 'Rascunho'
            : extraParams.status;
      chips.push({
        field: 'status',
        label: 'Status',
        chipValue: statusLabel,
        value: extraParams.status,
      });
    }

    const baseChips = stripStatusChip(
      stripPublishedChips(videoPageFilters.object || [])
    );
    return [...baseChips, ...chips];
  }

  function buildFilterQueryString(filter, extraQS) {
    return [filter, extraQS].filter(Boolean).join('&');
  }

  function buildFilterChips(filterObject) {
    const chips = [];
    if (publishedAtFrom) {
      chips.push({
        field: 'publishedAtFrom',
        label: 'Publicado de',
        chipValue: publishedAtFrom,
        value: publishedAtFrom,
      });
    }
    if (publishedAtTo) {
      chips.push({
        field: 'publishedAtTo',
        label: 'Publicado até',
        chipValue: publishedAtTo,
        value: publishedAtTo,
      });
    }
    if (
      !publishedAtFrom &&
      !publishedAtTo &&
      publishedAtYear &&
      publishedAtMonth
    ) {
      chips.push({
        field: 'publishedAtYm',
        label: 'Publicado em',
        chipValue: `${publishedAtMonth}/${publishedAtYear}`,
        value: `${publishedAtYear}-${publishedAtMonth}`,
      });
    }
    return [...(filterObject || []), ...chips];
  }

  useEffect(() => {
    refreshList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshData]);

  return (
    <SubjectLayout.Root id="main-videos">
      <Backdrop open={false} style={{ zIndex: 9999 }} sx={{ color: '#fff' }}>
        <CircularProgress color="inherit" size={60} />
      </Backdrop>

      <SubjectLayout.Title>Relatórios</SubjectLayout.Title>

      <Grid item xs={4} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <ExportMenu
          data={data}
          listKey={listKey}
          fetchPage={fetchPage}
          page={page}
          pageLimit={50}
          pdfTitle="Listagem de Vídeos"
          currentQS={videoPageFilters.qs}
          publishedAtFrom={publishedAtFrom}
          publishedAtTo={publishedAtTo}
        />
      </Grid>

      <SubjectLayout.InnerContainer>
        <div className="datatable_content">
          <div className="user_data_table_title" style={{ margin: 0 }}>
            <p style={{ margin: 0 }}>Listagem de Vídeos</p>
          </div>

          <div className="flex-filter">
            <Box
              sx={{
                width: '100%',
                height: '100%',
                display: 'grid',
                gridTemplateRows: `auto ${
                  (selectedItems?.[listId]?.length ?? 0) <= 4 ? 'auto' : '200px'
                }`,
              }}>
              <Table.Root
                data-no-dnd="true"
                sx={{ borderRadius: 0, display: 'grid' }}>
                <div style={{ width: '100%' }}>
                  <PublishedAtFilter
                    publishedAtFrom={publishedAtFrom}
                    setPublishedAtFrom={setPublishedAtFrom}
                    publishedAtTo={publishedAtTo}
                    setPublishedAtTo={setPublishedAtTo}
                    publishedAtMonth={publishedAtMonth}
                    setPublishedAtMonth={setPublishedAtMonth}
                    publishedAtYear={publishedAtYear}
                    setPublishedAtYear={setPublishedAtYear}
                    onClear={clearDateFilters}
                  />

                  <QuickFilters
                    activeKey={activeQuickKey}
                    onApply={filters => {
                      const {
                        publishedAtFrom: pFrom,
                        publishedAtTo: pTo,
                        publishedAtYear: pYear,
                        publishedAtMonth: pMonth,
                        extraParams,
                        preset,
                      } = filters;
                      setActiveQuickKey(preset?.key ?? null);
                      updateDateFilters(pFrom, pTo, pYear, pMonth);
                      const mergedQS = buildQueryString(
                        pFrom,
                        pTo,
                        pYear,
                        pMonth,
                        extraParams
                      );
                      const mergedChips = buildChips(
                        pFrom,
                        pTo,
                        pYear,
                        pMonth,
                        extraParams
                      );
                      setVideoPageFilter({ qs: mergedQS, object: mergedChips });
                      setPage(0);
                      getDataTable({ page: 0, filter: mergedQS });
                    }}
                  />

                  <Table.Filters
                    currentFilterValues={initialFilterData?.object}
                    onSubmit={(filter, filterObject) => {
                      const extraQS = buildPublishedAtQS();
                      const mergedQS = buildFilterQueryString(filter, extraQS);
                      const mergedObj = buildFilterChips(filterObject);

                      const usedManualDate =
                        !!publishedAtFrom ||
                        !!publishedAtTo ||
                        (!!publishedAtYear && !!publishedAtMonth);
                      if (usedManualDate) setActiveQuickKey(null);

                      setVideoPageFilter({ qs: mergedQS, object: mergedObj });
                      setPage(0);
                      getDataTable({ page: 0, filter: mergedQS });
                    }}
                    onClear={() => {
                      setPublishedAtFrom('');
                      setPublishedAtTo('');
                      setPublishedAtYear('');
                      setPublishedAtMonth('');
                      setActiveQuickKey(null);
                      setVideoPageFilter({ qs: '', object: [] });
                      setPage(0);
                      getDataTable({ page: 0 });
                    }}
                  />
                </div>

                <Table.Container>
                  <Table.DataView
                    isLoading={isLoading}
                    columns={defaultColumns}
                    limitExceeded={limitExceeded}
                    data={data}
                    type={'actions-videos'}
                  />
                </Table.Container>

                <Table.Pagination
                  page={page}
                  setPage={setPage}
                  isLoading={isLoading}
                  count={count}
                  rowsPerPage={25}
                  rowsPerPageOptions={[25]}
                  onChangePage={currentPage => {
                    getDataTable({
                      page: currentPage,
                      filter: videoPageFilters.qs,
                    });
                    setPage(currentPage);
                  }}
                />
              </Table.Root>
            </Box>
          </div>
        </div>
      </SubjectLayout.InnerContainer>

      <ModalTitle
        open={openTitle}
        createVideo={createVideo}
        onClose={() => setOpenTitle(false)}
        CRUD_STATES={CRUD_STATES}
        setAuth={setAuth}
        auth={auth}
      />
    </SubjectLayout.Root>
  );
};

export default ReportsHome;
